/**
 * API client — Axios instance + function-per-endpoint pattern.
 *
 * Every backend endpoint gets a typed function. The frontend never
 * constructs URLs directly — this file is the single source of truth
 * for API communication.
 *
 * Auth flow:
 * - Request interceptor attaches the short-lived Bearer access token
 * - Refresh credential stays in a Secure HttpOnly cookie, never localStorage
 * - Response interceptor rotates that cookie-backed session on a 401
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import i18n from "../i18n";
import { withDeviceBootstrapIndexedDbMutex } from "./deviceBootstrapMutex";

export { DeviceBootstrapUnavailableError } from "./deviceBootstrapMutex";

export const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Token helpers — browser-readable credentials exist only in this module's
// process memory. The refresh-session credential is an HttpOnly cookie set by
// the API. A page reload starts unauthenticated in memory and bootstraps via
// that cookie; localStorage/sessionStorage are never auth authority.
// ---------------------------------------------------------------------------

let accessToken: string | null = null;
let csrfToken: string | null = null;
let authMemoryEpoch = 0;
// Distinct from authMemoryEpoch: a work epoch remains stable while its login
// response hydrates /me, whereas this increments for every credential binding.
// Containment uses it to avoid revoking a G2 cookie that arrived while G1 was
// obtaining CSRF for its best-effort cleanup.
let authCredentialEpoch = 0;
// A late external-auth response can change the shared HttpOnly cookie after a
// newer in-memory identity has started. While that cookie is untrusted, normal
// 403/409 recovery must not bootstrap from it. Only a successful
// access-authenticated rebind of the known memory binding releases quarantine.
let refreshCookieQuarantined = false;
const AUTH_SYNC_CHANNEL = "careerforge-auth-session";
/**
 * Cross-tab messages are untrusted hints only. They deliberately contain no
 * credential, account id, session generation, or other authority; each tab
 * resolves the actual state from its own HttpOnly cookie bootstrap.
 */
export type AuthSyncMessage =
  | { type: "session-hint"; senderId: string }
  | { type: "logout-hint"; senderId: string };
const authChannel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(AUTH_SYNC_CHANNEL);

function newOpaqueTabId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const tabSenderId = newOpaqueTabId();

export class AuthMemoryInvalidatedError extends Error {
  constructor() {
    super("Authentication state changed while this operation was pending.");
    this.name = "AuthMemoryInvalidatedError";
  }
}

export class RefreshCookieQuarantinedError extends Error {
  constructor() {
    super("Refresh cookie is quarantined pending external-auth containment.");
    this.name = "RefreshCookieQuarantinedError";
  }
}

function assertAuthMemoryEpoch(expectedEpoch: number): void {
  if (expectedEpoch !== authMemoryEpoch) {
    throw new AuthMemoryInvalidatedError();
  }
}

function assertRefreshCookieUsable(expectedEpoch: number): void {
  assertAuthMemoryEpoch(expectedEpoch);
  if (refreshCookieQuarantined) {
    throw new RefreshCookieQuarantinedError();
  }
}

export function getAuthMemoryEpoch(): number {
  return authMemoryEpoch;
}

export function getAuthCredentialEpoch(): number {
  return authCredentialEpoch;
}

function assertAuthCredentialEpoch(expectedEpoch: number): void {
  if (expectedEpoch !== authCredentialEpoch) {
    throw new AuthMemoryInvalidatedError();
  }
}

/**
 * Fence normal refresh/bootstrap from a cookie that a stale OAuth or email
 * completion may have installed. This changes no browser credential and does
 * not itself trust or mutate the cookie. A known bearer may later replace the
 * unknown cookie only through the access-authenticated rebind endpoint.
 */
export function quarantineRefreshCookie(expectedEpoch = authMemoryEpoch): boolean {
  if (expectedEpoch !== authMemoryEpoch) return false;
  refreshCookieQuarantined = true;
  return true;
}

/**
 * Release a quarantine only after an access-authenticated rebind has replaced
 * the cookie with the current known session. Epoch mismatch deliberately
 * leaves the current quarantine alone.
 */
export function releaseRefreshCookieQuarantine(
  expectedEpoch = authMemoryEpoch,
): boolean {
  if (expectedEpoch !== authMemoryEpoch) return false;
  refreshCookieQuarantined = false;
  return true;
}

export function isRefreshCookieQuarantined(): boolean {
  return refreshCookieQuarantined;
}

export function subscribeToAuthSync(
  listener: (message: AuthSyncMessage) => void,
): () => void {
  if (!authChannel) return () => undefined;
  const handler = (event: MessageEvent<AuthSyncMessage>) => {
    const message = event.data;
    if (
      !message
      || message.senderId === tabSenderId
      || (message.type !== "session-hint" && message.type !== "logout-hint")
    ) {
      return;
    }
    listener(message);
  };
  authChannel.addEventListener("message", handler);
  return () => authChannel.removeEventListener("message", handler);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setTokens(
  nextAccessToken: string,
  nextCsrfToken: string,
  broadcast = true,
  expectedEpoch?: number,
): void {
  if (expectedEpoch === undefined) {
    // A direct login/OAuth completion supersedes every prior in-flight
    // rebootstrap. Internal refresh callers pass their expected epoch instead.
    authMemoryEpoch += 1;
  } else {
    assertAuthMemoryEpoch(expectedEpoch);
  }
  accessToken = nextAccessToken;
  csrfToken = nextCsrfToken;
  authCredentialEpoch += 1;
  // Do not clear a cookie quarantine merely because JavaScript received a
  // token pair. A stale OAuth/verification response can install its Set-Cookie
  // before this code runs. The owner of this authenticated transition must
  // explicitly rebind the cookie to this bearer, then release quarantine.
  if (broadcast) {
    authChannel?.postMessage({ type: "session-hint", senderId: tabSenderId });
  }
}

export function clearTokens(broadcast = true): void {
  // Invalidating memory before any awaited logout/refresh work prevents a
  // late response from reanimating the previous account.
  authMemoryEpoch += 1;
  authCredentialEpoch += 1;
  accessToken = null;
  csrfToken = null;
  if (broadcast) {
    authChannel?.postMessage({ type: "logout-hint", senderId: tabSenderId });
  }
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

// ---------------------------------------------------------------------------
// Request interceptor — attach Bearer token + Accept-Language to every request
//
// The browser's native Accept-Language header reflects OS/browser settings,
// NOT the language the user picked in-app (i18next + localStorage) — those
// are two independent things. Without this, get_locale's Accept-Language
// fallback (used for every anonymous/pre-auth request: login, register,
// password reset) silently ignores the user's in-app language choice and
// only self-corrects once they're signed in and user.locale is readable
// from the DB. Found via the i18n Phase 5 live spot-review: a wrong-password
// error rendered in English despite the UI being fully switched to Spanish.
// ---------------------------------------------------------------------------

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.headers && i18n.language) {
    config.headers["Accept-Language"] = i18n.language;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — refresh token on 401, retry original request
//
// Handles concurrent 401s: only one refresh in flight at a time.
// Other requests queue up and retry once the refresh completes.
// ---------------------------------------------------------------------------

let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function onRefreshComplete(newToken: string) {
  pendingRequests.forEach((p) => p.resolve(newToken));
  pendingRequests = [];
}

function onRefreshFailed(err: unknown) {
  pendingRequests.forEach((p) => p.reject(err));
  pendingRequests = [];
}

async function fetchCsrfToken(
  expectedEpoch = authMemoryEpoch,
  signal?: AbortSignal,
  persist = true,
  allowQuarantinedCookie = false,
): Promise<string> {
  if (allowQuarantinedCookie) {
    assertAuthMemoryEpoch(expectedEpoch);
  } else {
    assertRefreshCookieUsable(expectedEpoch);
  }
  const { data } = await axios.get<{ csrf_token: string }>(
    `${API_BASE}/auth/csrf`,
    { withCredentials: true, signal },
  );
  if (allowQuarantinedCookie) {
    assertAuthMemoryEpoch(expectedEpoch);
  } else {
    assertRefreshCookieUsable(expectedEpoch);
  }
  if (persist) {
    csrfToken = data.csrf_token;
  }
  return data.csrf_token;
}

interface RefreshOptions {
  /** Do not echo a remote tab's credential-free event back to it. */
  broadcast?: boolean;
  /** A caller may cancel a superseded remote bootstrap without clearing state. */
  signal?: AbortSignal;
  /** Internal epoch captured before rebootstrap; prevents stale writes. */
  expectedEpoch?: number;
}

const STALE_ROTATION_CSRF_BOOTSTRAP_ATTEMPTS = 2;

function waitForCookieUpdate(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function bootstrapCsrfFromSharedCookie(
  expectedEpoch: number,
  signal?: AbortSignal,
): Promise<string> {
  // A stale-rotation response can win the network race against the response
  // that carries the winning tab's Set-Cookie. Wait and read only from the
  // shared HttpOnly cookie a bounded number of times; never receive a
  // successor secret from the stale request itself.
  assertRefreshCookieUsable(expectedEpoch);
  csrfToken = null;
  let latestError: unknown;
  for (let attempt = 0; attempt < STALE_ROTATION_CSRF_BOOTSTRAP_ATTEMPTS; attempt += 1) {
    if (attempt) await waitForCookieUpdate(100);
    try {
      return await fetchCsrfToken(expectedEpoch, signal);
    } catch (error) {
      latestError = error;
    }
  }
  throw latestError;
}

async function rotateRefreshSession(
  { broadcast = true, signal, expectedEpoch = authMemoryEpoch }: RefreshOptions = {},
): Promise<TokenResponse> {
  // A sibling tab may have rotated the shared HttpOnly cookie. On stale CSRF
  // or an intentionally credential-free 409 predecessor response, bootstrap
  // from that cookie once and retry. A separate browser with only the old
  // cookie cannot get a successor secret from this flow.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    assertRefreshCookieUsable(expectedEpoch);
    const csrf = csrfToken || await fetchCsrfToken(expectedEpoch, signal);
    try {
      const { data } = await axios.post<TokenResponse>(
        `${API_BASE}/auth/refresh`,
        {},
        {
          withCredentials: true,
          headers: { "X-CSRF-Token": csrf },
          signal,
        },
      );
      assertRefreshCookieUsable(expectedEpoch);
      setTokens(data.access_token, data.csrf_token, false, expectedEpoch);
      if (broadcast) {
        authChannel?.postMessage({ type: "session-hint", senderId: tabSenderId });
      }
      return data;
    } catch (error) {
      const recoverable = axios.isAxiosError(error)
        && (error.response?.status === 403 || error.response?.status === 409);
      if (!recoverable || attempt) {
        throw error;
      }
      assertRefreshCookieUsable(expectedEpoch);
      await bootstrapCsrfFromSharedCookie(expectedEpoch, signal);
    }
  }
  throw new Error("Refresh retry exhausted");
}

/**
 * Replace an unknown refresh cookie with a refresh session for the current
 * in-memory access bearer. This endpoint does not receive or inspect the
 * browser cookie, so a late external-auth response cannot be adopted or
 * revoked accidentally. The caller must release any quarantine only after this
 * succeeds while the captured memory epoch remains current.
 */
export async function rebindCurrentRefreshBinding(
  expectedEpoch = authMemoryEpoch,
  signal?: AbortSignal,
  expectedCredentialEpoch = authCredentialEpoch,
): Promise<TokenResponse> {
  const csrf = csrfToken;
  const bearer = accessToken;
  if (!bearer || !csrf) {
    throw new AuthMemoryInvalidatedError();
  }
  assertAuthMemoryEpoch(expectedEpoch);
  assertAuthCredentialEpoch(expectedCredentialEpoch);
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data } = await axios.post<TokenResponse>(
        `${API_BASE}/auth/refresh/rebind`,
        {},
        {
          withCredentials: true,
          // Do not use the intercepting instance: this request must use the
          // bearer captured above, rather than a newer token a superseded async
          // caller might observe at dispatch time.
          headers: {
            Authorization: `Bearer ${bearer}`,
            "X-CSRF-Token": csrf,
            "X-Session-Rebind": "1",
          },
          signal,
        },
      );
      assertAuthMemoryEpoch(expectedEpoch);
      assertAuthCredentialEpoch(expectedCredentialEpoch);
      return data;
    } catch (error) {
      // Family locking can observe a just-consumed member during a normal
      // refresh. A bounded 409 retry uses the same bearer/device proof and
      // never reads or adopts the quarantined cookie.
      if (
        attempt === 0
        && axios.isAxiosError(error)
        && error.response?.status === 409
      ) {
        await new Promise((resolve) => window.setTimeout(resolve, 40));
        assertAuthMemoryEpoch(expectedEpoch);
        assertAuthCredentialEpoch(expectedCredentialEpoch);
        continue;
      }
      throw error;
    }
  }
  throw new Error("Refresh-session rebind retry exhausted.");
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only intercept 401s, and don't retry auth endpoints or already-retried requests
    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url?.startsWith("/auth/")
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Another request is already refreshing — queue this one
      return new Promise((resolve, reject) => {
        pendingRequests.push({
          resolve: (token: string) => {
            originalRequest._retry = true;
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;
    const refreshEpoch = authMemoryEpoch;

    try {
      // Call the cookie-authenticated refresh endpoint directly (not through
      // the intercepted instance) so a refresh failure cannot recurse.
      const data = await rotateRefreshSession({ expectedEpoch: refreshEpoch });
      onRefreshComplete(data.access_token);

      // Retry original request with new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
      }
      return api(originalRequest);
    } catch (refreshError) {
      onRefreshFailed(refreshError);
      // Containment owns the suspected external cookie. A generic 401
      // interceptor must neither clear/navigate nor bootstrap and adopt it.
      if (refreshError instanceof RefreshCookieQuarantinedError) {
        return Promise.reject(refreshError);
      }
      // A newer login/logout owns state if it advanced the epoch while this
      // refresh was in flight. Never clear or redirect that newer session.
      if (refreshEpoch === authMemoryEpoch) {
        clearTokens();
        window.location.href = "/login";
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Company {
  id: string;
  name: string;
  slug: string;
  principle_type: string;
}

export interface CompanyDetail extends Company {
  principles: Array<{ name: string; description: string }>;
  interview_focus: string;
  interview_tips: string[] | null;
  logo_url: string | null;
}

export interface Question {
  id: string;
  question_text: string;
  role_tags: string[];
  company_tags: string[];
  competency_tags: string[];
  difficulty: string;
  level_band: string | null;
  source: string;
  usage_count: number;
  track: string;               // "standard" | "agentic"
  interview_type: string;      // "behavioral" | "system_design"
  tags: string[];
  ideal_answer_points: string[];
}

export interface QuestionList {
  items: Question[];
  total: number;
  skip: number;
  limit: number;
}

export interface AnswerVersion {
  id: string;
  answer_id: string;
  version_number: number;
  answer_text: string;
  word_count: number | null;
  is_ai_assisted: boolean;
  created_at: string;
}

export interface Answer {
  id: string;
  user_id: string | null;
  question_id: string | null;
  custom_question_text: string | null;
  target_company_id: string;
  target_role: string;
  experience_level: string;
  version_count: number;
  best_average_score: number | null;
  created_at: string;
  updated_at: string;
  versions?: AnswerVersion[];
}

export interface AgenticScores {
  [dimension: string]: number;  // dimension_name → 0-100
}

export interface AgenticResult {
  scores: AgenticScores;
  hiring_decision: string;
  summary_feedback?: string;
  feedback_summary?: string;
  red_flags?: string[];
  missing_components?: string[];
  the_diff: {
    user_answer_critique?: string;
    staff_engineer_rewrite?: string;
    candidate_approach?: string;
    staff_architect_approach?: string;
  };
}

export interface Evaluation {
  id: string;
  answer_version_id: string;
  answer_id: string | null;        // parent answer — for revision flow
  answer_text: string | null;      // the answer text that was evaluated
  version_number: number | null;   // which version this evaluation is for
  question_id: string | null;      // source question — for "back to question" link
  question_text: string | null;    // resolved question text (bank or custom)
  status: "queued" | "analyzing" | "completed" | "failed";
  evaluation_type: string;         // "standard" | "agentic"

  // Standard STAR scores (1-5)
  situation_score: number | null;
  task_score: number | null;
  action_score: number | null;
  result_score: number | null;
  engagement_score: number | null;
  overall_score: number | null;
  average_score: number | null;

  // Agentic scores (0-100)
  agentic_scores: AgenticScores | null;
  agentic_result: AgenticResult | null;
  hiring_decision: string | null;

  evaluation_markdown: string | null;
  evaluation_sections: Record<string, any> | null;
  company_alignment: Record<string, any> | null;
  follow_up_questions: Array<Record<string, any>> | null;
  model_used: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  processing_seconds: number | null;
  error_message: string | null;
  share_token: string | null;
  shared_at: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Auth types
// ---------------------------------------------------------------------------

export interface TokenResponse {
  access_token: string;
  csrf_token: string;
  token_type: string;
}

export interface RegistrationResponse {
  detail: string;
  email_verification_required: true;
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  oauth_provider: string | null;
  default_role: string | null;
  default_experience_level: string | null;
  plan_tier: string;
  evaluations_this_month: number;
  is_moderator: boolean;
  is_coach: boolean;
  is_admin: boolean;
  /** UI language preference; present once backend task 0.5 ships. */
  locale?: string;
}

// ---------------------------------------------------------------------------
// Auth endpoints
// ---------------------------------------------------------------------------

export async function loginUser(payload: {
  email: string;
  password: string;
  accept_terms?: boolean;
  accept_privacy?: boolean;
}): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>("/auth/login", payload);
  return data;
}

let deviceBootstrapInFlight: Promise<void> | null = null;
const DEVICE_BOOTSTRAP_LOCK = "careerforge-device-bootstrap-v1";
const DEVICE_BOOTSTRAP_CHANNEL = `${DEVICE_BOOTSTRAP_LOCK}:coordination`;

type DeviceBootstrapMessage = { type: "complete"; senderId: string };

const deviceBootstrapChannel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(DEVICE_BOOTSTRAP_CHANNEL);

async function coordinateDeviceBootstrap(task: () => Promise<void>): Promise<void> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (locks) {
    await locks.request(DEVICE_BOOTSTRAP_LOCK, { mode: "exclusive" }, task);
    return;
  }
  // A Web Locks-free browser needs a *durable atomic* exclusion primitive. The
  // IndexedDB mutex stores only a random owner and short expiry, never a device
  // proof or session material. Failure to acquire is deliberately fail-closed:
  // do not make a concurrent bootstrap request based on a channel hint.
  await withDeviceBootstrapIndexedDbMutex(task, { owner: tabSenderId });
  // BroadcastChannel is notification-only. It has no participants/election
  // protocol and cannot grant the right to call the endpoint.
  deviceBootstrapChannel?.postMessage({ type: "complete", senderId: tabSenderId });
}

/**
 * Establish the stable device proof before any endpoint can mint a refresh
 * family. It is cookie-only and idempotent; ordinary auth responses never
 * rotate it, preventing concurrent completion responses from swapping it.
 */
export async function ensureDeviceBinding(signal?: AbortSignal): Promise<void> {
  // Do not attach a supersedable auth-work AbortSignal to the shared
  // idempotent bootstrap: an old G1 abort must not cancel G2's one stable
  // device setup. This operation grants no account/session authority.
  void signal;
  if (!deviceBootstrapInFlight) {
    deviceBootstrapInFlight = coordinateDeviceBootstrap(async () => {
      await axios.post(
        `${API_BASE}/auth/device/bootstrap`,
        {},
        {
          withCredentials: true,
          // Must remain shorter than the fallback IndexedDB mutex lease. A
          // failed/slow request settles before another tab can recover an
          // expired owner and enter the critical section.
          timeout: 10_000,
          // Required non-simple first-party mutation intent. Origin/Referer
          // validation happens server-side before any Set-Cookie is possible.
          headers: {
            "X-Device-Bootstrap": "1",
          },
        },
      );
    }).finally(() => {
      deviceBootstrapInFlight = null;
    });
  }
  return deviceBootstrapInFlight;
}

export function isPolicyConsentRequiredError(error: unknown): boolean {
  return axios.isAxiosError(error)
    && error.response?.status === 403
    && (error.response.data as { detail?: { code?: string } } | undefined)
      ?.detail?.code === "POLICY_CONSENT_REQUIRED";
}

export async function registerUser(payload: {
  email: string;
  password: string;
  display_name?: string;
  accept_terms: true;
  accept_privacy: true;
}): Promise<RegistrationResponse> {
  const { data } = await api.post<RegistrationResponse>("/auth/register", payload);
  return data;
}

export async function resendVerificationEmail(email: string): Promise<RegistrationResponse> {
  const { data } = await api.post<RegistrationResponse>("/auth/resend-verification", { email });
  return data;
}

let remoteRebootstrapInFlight:
  | { epoch: number; promise: Promise<TokenResponse> }
  | null = null;

export async function refreshTokens(options: RefreshOptions = {}): Promise<TokenResponse> {
  const expectedEpoch = options.expectedEpoch ?? authMemoryEpoch;
  assertRefreshCookieUsable(expectedEpoch);
  if (!options.broadcast) {
    // A burst of credential-free hints is coalesced only while it belongs to
    // the same in-memory auth epoch. A login/logout advances that epoch, so
    // a stale promise can neither be reused nor write credentials afterward.
    if (
      !remoteRebootstrapInFlight
      || remoteRebootstrapInFlight.epoch !== expectedEpoch
    ) {
      const promise = rotateRefreshSession({ ...options, expectedEpoch }).finally(() => {
        if (remoteRebootstrapInFlight?.epoch === expectedEpoch) {
          remoteRebootstrapInFlight = null;
        }
      });
      remoteRebootstrapInFlight = { epoch: expectedEpoch, promise };
    }
    return remoteRebootstrapInFlight.promise;
  }
  return rotateRefreshSession({ ...options, expectedEpoch });
}

// Exchange a single-use OAuth hand-off code (from the ?code= redirect) for a
// token pair. Keeps the refresh token out of the URL fragment / history (M-5).
export async function exchangeOAuthCode(
  code: string,
  signal?: AbortSignal,
): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>("/auth/oauth/exchange", {
    code,
  }, { signal });
  return data;
}

export async function beginOAuthRegistration(): Promise<void> {
  await api.post("/auth/oauth/consent", {
    accept_terms: true,
    accept_privacy: true,
  });
}

export async function verifyEmail(
  token: string,
  signal?: AbortSignal,
): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>(
    "/auth/verify-email",
    { token },
    { signal },
  );
  return data;
}

export async function logoutUser(
  csrfForLogout: string | null = null,
  signal?: AbortSignal,
  expectedEpoch = authMemoryEpoch,
  allowQuarantinedCookie = false,
  expectedCredentialEpoch = authCredentialEpoch,
): Promise<void> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    // Do not write a CSRF value back into memory during logout. The caller
    // clears auth state first, and a late logout helper must not resurrect it.
    assertAuthMemoryEpoch(expectedEpoch);
    assertAuthCredentialEpoch(expectedCredentialEpoch);
    let csrf: string;
    try {
      csrf = csrfForLogout || await fetchCsrfToken(
        expectedEpoch,
        signal,
        false,
        allowQuarantinedCookie,
      );
    } catch (error) {
      // /auth/csrf returns 401 while clearing an already-invalid cookie. For
      // explicit stale-external containment that is a completed cleanup, not
      // a reason to retain a dead quarantine forever.
      if (
        allowQuarantinedCookie
        && axios.isAxiosError(error)
        && error.response?.status === 401
      ) {
        return;
      }
      throw error;
    }
    assertAuthMemoryEpoch(expectedEpoch);
    assertAuthCredentialEpoch(expectedCredentialEpoch);
    try {
      await axios.post(
        `${API_BASE}/auth/logout`,
        {},
        {
          withCredentials: true,
          headers: { "X-CSRF-Token": csrf },
          signal,
        },
      );
      return;
    } catch (error) {
      const recoverable = axios.isAxiosError(error)
        && (error.response?.status === 403 || error.response?.status === 409);
      if (!recoverable || attempt) {
        throw error;
      }
      csrfForLogout = await fetchCsrfToken(
        expectedEpoch,
        signal,
        false,
        allowQuarantinedCookie,
      );
      assertAuthCredentialEpoch(expectedCredentialEpoch);
    }
  }
}

export async function getMe(signal?: AbortSignal): Promise<AuthUser> {
  const { data } = await api.get<AuthUser>("/auth/me", { signal });
  return data;
}

/** Persist the user's UI language preference (I18N_PLAN.md 0.2/0.5).
 *  Backend resolves locale as user.locale → Accept-Language → "en". */
export async function updateMyLocale(locale: string): Promise<AuthUser> {
  const { data } = await api.patch<AuthUser>("/auth/me", { locale });
  return data;
}

export async function requestPasswordReset(email: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(
    "/auth/password-reset/request",
    { email },
  );
  return data;
}

export async function confirmPasswordReset(payload: {
  token: string;
  new_password: string;
}): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(
    "/auth/password-reset/confirm",
    payload,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Company endpoints
// ---------------------------------------------------------------------------

export async function listCompanies(): Promise<Company[]> {
  const { data } = await api.get<Company[]>("/api/v1/companies");
  return data;
}

export async function getCompany(id: string): Promise<CompanyDetail> {
  const { data } = await api.get<CompanyDetail>(`/api/v1/companies/${id}`);
  return data;
}

// ---------------------------------------------------------------------------
// Question endpoints
// ---------------------------------------------------------------------------

export async function listQuestions(params?: {
  role?: string;
  competency?: string;
  difficulty?: string;
  level?: string;
  company?: string;
  search?: string;
  skip?: number;
  limit?: number;
}): Promise<QuestionList> {
  const { data } = await api.get<QuestionList>("/api/v1/questions", { params });
  return data;
}

export async function getRandomQuestion(params?: {
  role?: string;
  competency?: string;
  difficulty?: string;
  level?: string;
}): Promise<Question> {
  const { data } = await api.get<Question>("/api/v1/questions/random", {
    params,
  });
  return data;
}

// Community question submission
export async function submitQuestion(payload: {
  question_text: string;
  role_tags?: string[];
  company_tags?: string[];
  competency_tags?: string[];
  difficulty?: string;
  level_band?: string;
}): Promise<Question> {
  const { data } = await api.post<Question>("/api/v1/questions", payload);
  return data;
}

// ---------------------------------------------------------------------------
// Question Bookmarks
// ---------------------------------------------------------------------------

export async function bookmarkQuestion(questionId: string): Promise<void> {
  await api.post(`/api/v1/questions/${questionId}/bookmark`);
}

export async function unbookmarkQuestion(questionId: string): Promise<void> {
  await api.delete(`/api/v1/questions/${questionId}/bookmark`);
}

export async function getBookmarkedQuestionIds(): Promise<string[]> {
  const { data } = await api.get<string[]>("/api/v1/questions/bookmarked/ids");
  return data;
}

// Moderation types & endpoints
export interface ModerationQuestion {
  id: string;
  question_text: string;
  role_tags: string[];
  company_tags: string[];
  competency_tags: string[];
  difficulty: string;
  level_band: string | null;
  source: string;
  status: string;
  submitted_by_user_id: string | null;
  submitted_by_email: string | null;
  moderation_notes: string | null;
  moderated_at: string | null;
  created_at: string | null;
}

export async function getPendingQuestions(
  skip = 0,
  limit = 50
): Promise<ModerationQuestion[]> {
  const { data } = await api.get<ModerationQuestion[]>(
    "/api/v1/questions/moderation/pending",
    { params: { skip, limit } }
  );
  return data;
}

export async function moderateQuestion(
  questionId: string,
  payload: { action: "approve" | "reject"; notes?: string }
): Promise<ModerationQuestion> {
  const { data } = await api.patch<ModerationQuestion>(
    `/api/v1/questions/${questionId}/moderation`,
    payload
  );
  return data;
}

// ---------------------------------------------------------------------------
// Coaching endpoints
// ---------------------------------------------------------------------------

export interface CoachingRelationship {
  id: string;
  coach_id: string;
  coach_email: string;
  coach_name: string | null;
  student_id: string;
  student_email: string;
  student_name: string | null;
  status: string;
  created_at: string | null;
  accepted_at: string | null;
}

export interface StudentSummary {
  student_id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  total_evaluations: number;
  average_score: number | null;
  best_score: number | null;
  latest_evaluation_date: string | null;
  relationship_id: string;
}

export interface CoachDashboard {
  students: StudentSummary[];
  total_students: number;
  pending_invites: number;
}

export interface StudentEvaluation {
  evaluation_id: string;
  answer_id: string;
  question_text: string | null;
  company_name: string | null;
  target_role: string;
  average_score: number | null;
  situation_score: number | null;
  task_score: number | null;
  action_score: number | null;
  result_score: number | null;
  engagement_score: number | null;
  overall_score: number | null;
  status: string;
  created_at: string;
  version_number: number | null;
  coach_notes: CoachNotes | null;
}

export interface CoachNotes {
  notes: string;
  focus_areas: string[];
  coach_id: string;
  coach_name: string | null;
  updated_at: string | null;
}

// Coach endpoints
export async function inviteStudent(email: string): Promise<CoachingRelationship> {
  const { data } = await api.post<CoachingRelationship>("/api/v1/coaching/invite", { email });
  return data;
}

export async function getCoachStudents(): Promise<CoachDashboard> {
  const { data } = await api.get<CoachDashboard>("/api/v1/coaching/students");
  return data;
}

export async function getStudentEvaluations(studentId: string): Promise<StudentEvaluation[]> {
  const { data } = await api.get<StudentEvaluation[]>(
    `/api/v1/coaching/students/${studentId}/evaluations`
  );
  return data;
}

export async function updateCoachNotes(
  evaluationId: string,
  payload: { notes: string; focus_areas?: string[] }
): Promise<CoachNotes> {
  const { data } = await api.patch<CoachNotes>(
    `/api/v1/coaching/evaluations/${evaluationId}/coach-notes`,
    payload
  );
  return data;
}

// Student endpoints
export async function getMyInvites(): Promise<CoachingRelationship[]> {
  const { data } = await api.get<CoachingRelationship[]>("/api/v1/coaching/invites");
  return data;
}

export async function acceptInvite(inviteId: string): Promise<CoachingRelationship> {
  const { data } = await api.post<CoachingRelationship>(
    `/api/v1/coaching/invites/${inviteId}/accept`
  );
  return data;
}

export async function declineInvite(inviteId: string): Promise<void> {
  await api.post(`/api/v1/coaching/invites/${inviteId}/decline`);
}

export async function getMyCoaches(): Promise<CoachingRelationship[]> {
  const { data } = await api.get<CoachingRelationship[]>("/api/v1/coaching/my-coaches");
  return data;
}

export async function removeRelationship(relationshipId: string): Promise<void> {
  await api.delete(`/api/v1/coaching/relationships/${relationshipId}`);
}

// ---------------------------------------------------------------------------
// Answer endpoints
// ---------------------------------------------------------------------------

export async function createAnswer(payload: {
  question_id?: string;
  custom_question_text?: string;
  target_company_id?: string; // omitted on the agentic track
  target_role: string;
  experience_level: string;
  answer_text: string;
  is_ai_assisted?: boolean;
  track?: string;
  interview_type?: string;
}): Promise<Answer> {
  const { data } = await api.post<Answer>("/api/v1/answers", payload);
  return data;
}

export async function getAnswer(id: string): Promise<Answer> {
  const { data } = await api.get<Answer>(`/api/v1/answers/${id}`);
  return data;
}

export interface VersionScoreSummary {
  id: string;
  version_number: number;
  word_count: number | null;
  is_ai_assisted: boolean;
  created_at: string;
  evaluation_id: string | null;
  evaluation_status: string | null;
  situation_score: number | null;
  task_score: number | null;
  action_score: number | null;
  result_score: number | null;
  engagement_score: number | null;
  overall_score: number | null;
  average_score: number | null;
}

export interface AnswerComparison extends Answer {
  version_scores: VersionScoreSummary[];
}

export async function getAnswerComparison(
  id: string
): Promise<AnswerComparison> {
  const { data } = await api.get<AnswerComparison>(
    `/api/v1/answers/${id}/compare`
  );
  return data;
}

export async function createVersion(
  answerId: string,
  payload: { answer_text: string; is_ai_assisted?: boolean }
): Promise<AnswerVersion> {
  const { data } = await api.post<AnswerVersion>(
    `/api/v1/answers/${answerId}/versions`,
    payload
  );
  return data;
}

// ---------------------------------------------------------------------------
// Bulk Import
// ---------------------------------------------------------------------------

export interface ImportAnswerItem {
  answer_id: string;
  question_text: string | null;
  word_count: number;
}

export interface ImportResponse {
  imported_count: number;
  total_found: number;
  answers: ImportAnswerItem[];
  errors: string[];
}

export async function importAnswers(
  file: File,
  targetCompanyId: string,
  targetRole: string,
  experienceLevel: string
): Promise<ImportResponse> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("target_company_id", targetCompanyId);
  formData.append("target_role", targetRole);
  formData.append("experience_level", experienceLevel);

  // Pass the FormData directly and let the browser set the multipart
  // Content-Type WITH its boundary. Forcing the bare "multipart/form-data"
  // string strips the boundary, so the server can't parse the body.
  const { data } = await api.post<ImportResponse>(
    "/api/v1/answers/import",
    formData
  );
  return data;
}

// ---------------------------------------------------------------------------
// Speech-to-Text (dictation)
// ---------------------------------------------------------------------------

export interface TranscribeResponse {
  text: string;
}

/**
 * Transcribe a recorded audio blob via the server-side STT endpoint.
 *
 * Sends the blob as multipart/form-data. We pass the FormData directly and
 * OMIT any Content-Type header so the browser/axios adapter computes it,
 * including the required multipart boundary. (Setting a bare
 * "multipart/form-data" string strips the boundary and the server cannot
 * parse the upload — every request 400s.) Auth + 401-refresh come for free
 * from the shared `api` interceptors. The filename is cosmetic: the backend
 * keys off the part's Content-Type (the Blob's `type`), not the extension.
 *
 * Paid-tier gated + rate-limited server-side (403 / 413 / 429 / 502 surface
 * as AxiosErrors for the caller to handle).
 */
export async function transcribeAudio(
  blob: Blob,
): Promise<TranscribeResponse> {
  const formData = new FormData();
  formData.append("file", blob, "audio.webm");

  const { data } = await api.post<TranscribeResponse>(
    "/api/v1/transcribe",
    formData,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Evaluation endpoints
// ---------------------------------------------------------------------------

export async function createEvaluation(
  answerVersionId: string
): Promise<Evaluation> {
  const { data } = await api.post<Evaluation>("/api/v1/evaluations", {
    answer_version_id: answerVersionId,
  });
  return data;
}

export async function getEvaluation(id: string): Promise<Evaluation> {
  const { data } = await api.get<Evaluation>(`/api/v1/evaluations/${id}`);
  return data;
}

export function getEvaluationPdfUrl(id: string): string {
  return `${API_BASE}/api/v1/evaluations/${id}/report/pdf`;
}

// Sharing
export interface ShareResponse {
  share_token: string;
  share_url: string;
}

export interface SharedEvaluation {
  company_name: string | null;
  target_role: string | null;
  question_text: string | null;
  situation_score: number | null;
  task_score: number | null;
  action_score: number | null;
  result_score: number | null;
  engagement_score: number | null;
  overall_score: number | null;
  average_score: number | null;
  evaluation_markdown: string | null;
  evaluation_sections: Record<string, any> | null;
  company_alignment: Record<string, any> | null;
  follow_up_questions: Array<Record<string, any>> | null;
  created_at: string;
}

export async function shareEvaluation(
  evaluationId: string
): Promise<ShareResponse> {
  const { data } = await api.post<ShareResponse>(
    `/api/v1/evaluations/${evaluationId}/share`
  );
  return data;
}

export async function revokeShare(evaluationId: string): Promise<void> {
  await api.delete(`/api/v1/evaluations/${evaluationId}/share`);
}

export async function getSharedEvaluation(
  token: string
): Promise<SharedEvaluation> {
  const { data } = await api.get<SharedEvaluation>(
    `/api/v1/evaluations/shared/${token}`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Inline Suggestions
// ---------------------------------------------------------------------------

export interface SuggestionItem {
  section: string;
  suggestion: string;
  example: string;
}

export interface SuggestionsResponse {
  suggestions: SuggestionItem[];
  message?: string;
  input_tokens?: number;
  output_tokens?: number;
}

export async function getEvaluationSuggestions(
  evaluationId: string
): Promise<SuggestionsResponse> {
  const { data } = await api.post<SuggestionsResponse>(
    `/api/v1/evaluations/${evaluationId}/suggestions`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Dashboard endpoints
// ---------------------------------------------------------------------------

export interface BadgeInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlocked_at: string | null;
}

export interface StreakInfo {
  current_streak: number;
  longest_streak: number;
  last_practice_date: string | null;
  streak_active: boolean;
}

export interface DashboardStats {
  total_evaluations: number;
  average_score: number | null;
  best_score: number | null;
  total_answers: number;
  evaluations_this_month: number;
  streak: StreakInfo | null;
  badges: BadgeInfo[];
}

export interface RecentEvaluation {
  evaluation_id: string;
  answer_id: string;
  question_text: string | null;
  company_name: string | null;
  target_role: string;
  average_score: number | null;
  status: string;
  created_at: string;
  version_number: number | null;
}

export interface ScoreDataPoint {
  evaluation_id: string;
  date: string;
  situation: number | null;
  task: number | null;
  action: number | null;
  result: number | null;
  engagement: number | null;
  overall: number | null;
  average: number | null;
  company_name: string | null;
  target_role: string | null;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  const { data } = await api.get<DashboardStats>("/api/v1/dashboard/stats");
  return data;
}

export async function getRecentEvaluations(
  limit = 10
): Promise<RecentEvaluation[]> {
  const { data } = await api.get<RecentEvaluation[]>(
    "/api/v1/dashboard/recent",
    { params: { limit } }
  );
  return data;
}

export async function getScoreHistory(
  limit = 30
): Promise<ScoreDataPoint[]> {
  const { data } = await api.get<ScoreDataPoint[]>(
    "/api/v1/dashboard/score-history",
    { params: { limit } }
  );
  return data;
}

// ---------------------------------------------------------------------------
// Recommended Questions (Spaced Repetition)
// ---------------------------------------------------------------------------

export interface RecommendedQuestion {
  question_id: string | null;
  question_text: string;
  company_name: string;
  target_role: string;
  best_score: number;
  last_practiced: string;
  days_since_practice: number;
  priority: number;
  answer_id: string;
}

export async function getRecommendedQuestions(
  limit = 5
): Promise<RecommendedQuestion[]> {
  const { data } = await api.get<RecommendedQuestion[]>(
    "/api/v1/dashboard/recommended",
    { params: { limit } }
  );
  return data;
}

// ---------------------------------------------------------------------------
// Analytics endpoints
// ---------------------------------------------------------------------------

export interface DimensionAverage {
  dimension: string;
  average: number;
  count: number;
}

export interface CompanyBreakdown {
  company_name: string;
  evaluation_count: number;
  average_score: number | null;
  best_score: number | null;
  situation_avg: number | null;
  task_avg: number | null;
  action_avg: number | null;
  result_avg: number | null;
  engagement_avg: number | null;
  overall_avg: number | null;
}

export interface ReadinessScore {
  overall_readiness: number;
  score_component: number;
  consistency_component: number;
  trend_component: number;
  label: string;
}

export interface AnalyticsData {
  dimension_averages: DimensionAverage[];
  company_breakdowns: CompanyBreakdown[];
  readiness: ReadinessScore | null;
}

export async function getAnalytics(): Promise<AnalyticsData> {
  const { data } = await api.get<AnalyticsData>("/api/v1/dashboard/analytics");
  return data;
}

// ---------------------------------------------------------------------------
// Mock Interview endpoints
// ---------------------------------------------------------------------------

export interface MockSession {
  id: string;
  question_id: string;
  question_text: string;
  time_limit_seconds: number;
  time_used_seconds: number | null;
  completed: boolean;
}

export async function startMockSession(payload: {
  question_id: string;
  time_limit_seconds?: number;
}): Promise<MockSession> {
  const { data } = await api.post<MockSession>("/api/v1/mock", payload);
  return data;
}

export async function completeMockSession(
  sessionId: string,
  payload: {
    time_used_seconds: number;
    answer_version_id?: string;
    evaluation_id?: string;
  }
): Promise<MockSession> {
  const { data } = await api.patch<MockSession>(
    `/api/v1/mock/${sessionId}`,
    payload
  );
  return data;
}

// ---------------------------------------------------------------------------
// AI Generator endpoints
// ---------------------------------------------------------------------------

export interface GenerateResult {
  answer_text: string;
  word_count: number;
  model_used: string;
  input_tokens: number;
  output_tokens: number;
  processing_seconds: number;
}

export async function generateAnswer(payload: {
  question_text: string;
  company_name: string;
  target_role: string;
  experience_level: string;
  situation_bullets: string;
  task_bullets: string;
  action_bullets: string;
  result_bullets: string;
}): Promise<GenerateResult> {
  const { data } = await api.post<GenerateResult>("/api/v1/generator", payload);
  return data;
}

// ---------------------------------------------------------------------------
// Billing endpoints
// ---------------------------------------------------------------------------

export interface BillingStatus {
  plan_tier: string;
  evaluations_this_month: number;
  evaluation_limit: number;
  stripe_customer_id: string | null;
}

export interface CheckoutResponse {
  checkout_url: string;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  const { data } = await api.get<BillingStatus>("/billing/status");
  return data;
}

export async function createCheckoutSession(params: {
  tier?: "pro" | "all_access";
  cadence?: "monthly" | "annual";
  promo?: string | null;
} = {}): Promise<CheckoutResponse> {
  const { data } = await api.post<CheckoutResponse>("/billing/checkout", {
    tier: params.tier ?? "pro",
    cadence: params.cadence ?? "monthly",
    promo: params.promo ?? null,
  });
  return data;
}

/**
 * One-time checkout for a standalone course (D-010/D-011).
 *
 * Distinct endpoint from createCheckoutSession, not a parameter on it: the
 * backend runs mode="payment" here, and the webhook branches on a metadata key
 * only this path writes. Answers 400 when the user already owns the course —
 * either via All-Access (D-012) or a prior purchase — and 503 when no price is
 * configured, so callers should surface `detail` rather than a generic failure.
 */
export async function createCourseCheckoutSession(
  product = "claude-harness",
): Promise<CheckoutResponse> {
  const { data } = await api.post<CheckoutResponse>("/billing/checkout/course", {
    product,
  });
  return data;
}

export async function createPortalSession(): Promise<{ portal_url: string }> {
  const { data } = await api.post<{ portal_url: string }>("/billing/portal");
  return data;
}

// ---------------------------------------------------------------------------
// Answer Template endpoints
// ---------------------------------------------------------------------------

export interface AnswerTemplate {
  id: string;
  user_id: string;
  name: string;
  template_text: string;
  role_tags: string[];
  competency_tags: string[];
  is_default: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export async function listTemplates(): Promise<AnswerTemplate[]> {
  const { data } = await api.get<AnswerTemplate[]>("/api/v1/templates");
  return data;
}

export async function getTemplate(id: string): Promise<AnswerTemplate> {
  const { data } = await api.get<AnswerTemplate>(`/api/v1/templates/${id}`);
  return data;
}

export async function createTemplate(payload: {
  name: string;
  template_text: string;
  role_tags?: string[];
  competency_tags?: string[];
  is_default?: boolean;
}): Promise<AnswerTemplate> {
  const { data } = await api.post<AnswerTemplate>("/api/v1/templates", payload);
  return data;
}

export async function updateTemplate(
  id: string,
  payload: {
    name?: string;
    template_text?: string;
    role_tags?: string[];
    competency_tags?: string[];
    is_default?: boolean;
  }
): Promise<AnswerTemplate> {
  const { data } = await api.put<AnswerTemplate>(
    `/api/v1/templates/${id}`,
    payload
  );
  return data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await api.delete(`/api/v1/templates/${id}`);
}

// ---------------------------------------------------------------------------
// Agentic Scenarios endpoints
// ---------------------------------------------------------------------------

export interface Scenario {
  id: string;
  track: string;
  type: string;             // "behavioral" | "system_design"
  role: string;
  category: string;
  difficulty: string;
  question: string;
  tags: string[];
  ideal_answer_points: string[];
}

export interface ScenarioList {
  items: Scenario[];
  total: number;
}

export async function listScenarios(params?: {
  track?: string;
  role?: string;
  category?: string;
  interview_type?: string;
  difficulty?: string;
}): Promise<ScenarioList> {
  const { data } = await api.get<ScenarioList>("/api/v1/scenarios", { params });
  return data;
}

export async function getRandomScenario(params?: {
  track?: string;
  category?: string;
  interview_type?: string;
  difficulty?: string;
}): Promise<Scenario> {
  const { data } = await api.get<Scenario>("/api/v1/scenarios/random", { params });
  return data;
}

export async function getScenarioCategories(
  track = "agentic",
  interviewType?: string,
): Promise<string[]> {
  const { data } = await api.get<string[]>("/api/v1/scenarios/categories", {
    params: { track, interview_type: interviewType || undefined },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Course endpoints (Agentic AI Interviewing)
// ---------------------------------------------------------------------------

export interface LevelProgress {
  level: number;
  title: string;
  completed: boolean;
  badge_earned: boolean;
  practices_count: number;
  required_practices: number;
  locked: boolean;
  practiced_question_ids: string[];
}

export interface CourseProgressResponse {
  levels: LevelProgress[];
}

export interface CompleteLevelResponse {
  level: number;
  badge_name: string;
  next_level_unlocked: boolean;
}

export async function getCourseProgress(): Promise<CourseProgressResponse> {
  const { data } = await api.get<CourseProgressResponse>("/api/v1/course/progress");
  return data;
}

export async function getLevelQuestions(level: number): Promise<Question[]> {
  const { data } = await api.get<Question[]>(`/api/v1/course/level/${level}/questions`);
  return data;
}

export async function completeCourseLevel(level: number): Promise<CompleteLevelResponse> {
  const { data } = await api.post<CompleteLevelResponse>(`/api/v1/course/level/${level}/complete`);
  return data;
}

// ---------------------------------------------------------------------------
// Resume Mastery — ATS Scorer
// ---------------------------------------------------------------------------

export interface ResumeDimensionScore {
  name: string;
  score: number;
  rationale: string;
  improvements: string[];
}

export interface ResumeKeywordMatch {
  keyword: string;
  priority: "critical" | "high" | "medium";
  status: "present" | "missing" | "needs_emphasis";
}

export interface ResumeScoreResponse {
  overall_score: number;
  severity: "Strong Apply" | "Apply with Prep" | "Opportunistic" | "Do Not Apply";
  summary: string;
  dimensions: ResumeDimensionScore[];
  keyword_matches: ResumeKeywordMatch[];
  agentic_readiness: {
    d1: number; d2: number; d3: number; d4: number; d5: number; d6: number; d7: number;
  };
  top_improvements: string[];
  remaining_evaluations_this_month?: number;
}

export async function scoreResume(payload: {
  resume_text: string;
  job_description: string;
}): Promise<ResumeScoreResponse> {
  const { data } = await api.post<ResumeScoreResponse>("/api/v1/resume/score", payload);
  return data;
}

// ---------------------------------------------------------------------------
// LinkedIn Presence AI (D4) — profile analyzer (free) + post generator (AA)
//
// The analyzer must work logged-out: the shared `api` instance's request
// interceptor attaches Authorization only when a token exists, so anonymous
// callers send no auth header and hit the per-IP anon cap path. Paste-text
// only — there is deliberately no `url` field anywhere here.
// ---------------------------------------------------------------------------

export type LinkedInDimensionKey =
  | "headline_clarity"
  | "keyword_alignment"
  | "story_coherence"
  | "credibility_signals"
  | "cta_strength";

export interface LinkedInAnalyzeRequest {
  headline: string;
  about: string;
  experience: string;
  target_role: string;
}

export interface LinkedInDimensionResult {
  key: LinkedInDimensionKey;
  score: number; // 0–100
  reading: string;
  rewrite: string;
}

export interface LinkedInAnalyzeResult {
  dimensions: LinkedInDimensionResult[];
  overall: number; // 0–100
  summary: string;
}

export type LinkedInPostTone = "practitioner" | "story" | "contrarian" | "teacher";

export interface LinkedInPostRequest {
  topic: string;
  pov: string;
  audience: string;
  tone: LinkedInPostTone;
  positioning?: string | null;
}

export interface LinkedInPostVariant {
  hook: string;
  body: string;
  cta: string;
  hashtags: string[];
}

export interface LinkedInPostsResult {
  variants: LinkedInPostVariant[];
}

export async function analyzeLinkedInProfile(
  payload: LinkedInAnalyzeRequest,
): Promise<LinkedInAnalyzeResult> {
  const { data } = await api.post<LinkedInAnalyzeResult>("/api/v1/linkedin/analyze", payload);
  return data;
}

export async function generateLinkedInPosts(
  payload: LinkedInPostRequest,
): Promise<LinkedInPostsResult> {
  const { data } = await api.post<LinkedInPostsResult>("/api/v1/linkedin/posts", payload);
  return data;
}

// ---------------------------------------------------------------------------
// Career Transition — AI roadmap generator
// ---------------------------------------------------------------------------

// Phase 4a/4c (2026-04-26/27): RoadmapResponse rewritten to match the
// Roadmap Viewer v1 + Readiness Result v1 specs the backend now emits.
// Both pages consume this shape.

export type RoadmapTier = "gap" | "building" | "strong";
export type RoadmapPhaseStatus = "done" | "current" | "upcoming";
export type RoadmapConfidence = "High" | "Medium" | "Low";
export type RoadmapPace = "Minimum" | "Standard Build" | "Intensive";
export type RoadmapDueDay =
  | "Mon"
  | "Tue"
  | "Wed"
  | "Thu"
  | "Fri"
  | "Sat"
  | "Sun";
export type RoadmapSignalKind = "pos" | "neg" | "neutral";
export type RoadmapDimensionSlug =
  | "technical"
  | "portfolio"
  | "narrative"
  | "network"
  | "interview";
export type RoadmapPhaseSlug =
  | "foundation"
  | "evidence"
  | "visibility"
  | "interview";
export type RoadmapProductSlug =
  | "agentic-mastery"
  | "portfolio-kit"
  | "resume-mastery"
  | "linkedin-presence"
  | "interview-playbook"
  | "ai-interview-coach"
  | "career-transition"
  | "assessment";

export interface RoadmapProductRoute {
  product_slug: RoadmapProductSlug;
  label: string;
  href: string;
}

export interface RoadmapDimensionSignal {
  kind: RoadmapSignalKind;
  label: string;
}

export interface RoadmapDimension {
  slug: RoadmapDimensionSlug;
  title: string;
  subtitle: string;
  score: number;
  tier: RoadmapTier;
  reading: string;
  emphasis: string;
  signals: RoadmapDimensionSignal[];
}

export interface RoadmapArchetype {
  slug: string;
  name: string;
  confidence: RoadmapConfidence;
  hypothesis: string;
}

export interface RoadmapOverall {
  score: number;
  tier: RoadmapTier;
  weeks_to_ready: number;
  pace: RoadmapPace;
}

export interface RoadmapTask {
  id: string;
  title: string;
  summary: string;
  checklist: string[];
  estimate_hours: number;
  due_day: RoadmapDueDay | null;
  routes: RoadmapProductRoute[];
  primary_cta: RoadmapProductRoute | null;
  is_focus: boolean;
  blocked: boolean;
  done: boolean;
}

export interface RoadmapWeek {
  index: number;
  title: string;
  status: RoadmapPhaseStatus;
  tasks: RoadmapTask[];
}

export interface RoadmapPhase {
  index: number;
  slug: RoadmapPhaseSlug;
  title: string;
  week_range: [number, number];
  status: RoadmapPhaseStatus;
  summary: string;
  weeks: RoadmapWeek[];
}

export interface RoadmapUsage {
  tier: string;
  generations_used: number;
  limit_display: string;
  can_generate_again: boolean;
}

export interface LadderRow {
  hours_band: string;
  weeks_low: number;
  weeks_high: number;
}
export interface TrackEstimate {
  scoped_hours: [number, number];
  weeks: [number, number];
  ladder: LadderRow[];
  distinction_uplift_hours: [number, number];
}
export interface EffortEstimate {
  recommended_track: "knowledge" | "portfolio";
  track_rationale: string;
  knowledge: TrackEstimate;
  portfolio: TrackEstimate;
  experience_tier: string;
  experience_multiplier: number;
  effective_weekly: [number, number];
  calibration_note: string;
}

export interface RoadmapResponse {
  id: string;
  created_at: string;
  started_at: string;
  target_at: string;
  last_revised_at: string;
  archetype: RoadmapArchetype;
  overall: RoadmapOverall;
  target_audience: string;
  dimensions: RoadmapDimension[];
  means_it_does: string[];
  means_it_does_not: string[];
  primary_risk: string;
  key_insight: string;
  phases: RoadmapPhase[];
  compensation_guidance: string;
  financial_bridge_recommendation: string;
  internal_vs_external_recommendation: string;
  milestones_to_watch: string[];
  honest_limitations: string;
  usage: RoadmapUsage;
  estimate?: EffortEstimate | null;
  assessment?: Record<string, unknown> | null;
}

export interface RoadmapHistoryItem {
  id: string;
  created_at: string;
  readiness_score: number;
  archetype_key: string;
  target_audience: string;
}

export interface RoadmapGenerateAccepted {
  job_id: string;
  status: string;
  usage: RoadmapResponse["usage"];
}

export interface RoadmapJobStatus {
  job_id: string;
  status: "running" | "complete" | "failed";
  roadmap_id: string | null;
  error: string | null;
}

// 202 + job_id: generation runs server-side in the background; poll
// getRoadmapJobStatus. (2026-07-08 incident: the old ~90s synchronous
// request could die between edge and browser AFTER the backend committed.)
export async function generateRoadmap(
  assessment: Record<string, unknown>,
): Promise<RoadmapGenerateAccepted> {
  const { data } = await api.post<RoadmapGenerateAccepted>(
    "/api/v1/career-transition/roadmap/generate",
    { assessment },
  );
  return data;
}

export async function getRoadmapJobStatus(jobId: string): Promise<RoadmapJobStatus> {
  // Per-request timeout: without it a single hung GET can stall the poll
  // loop past its own deadline (the deadline is only checked between polls).
  const { data } = await api.get<RoadmapJobStatus>(
    `/api/v1/career-transition/roadmap/generate/status/${jobId}`,
    { timeout: 15_000 },
  );
  return data;
}

// A "transit" failure is one where the backend never gave a definitive
// answer: no HTTP response at all (network drop, poll timeout, hung request)
// or a 5xx. A 4xx IS the answer (quota, auth, validation) — post-failure
// roadmap recovery must not run for those, or it masks the real error.
export function isTransitFailure(status: number | undefined): boolean {
  return status === undefined || status >= 500;
}

// Poll a generation job to a terminal state. Resolves with the terminal
// status; throws Error("generation_poll_timeout") past timeoutMs.
export async function pollRoadmapJob(
  jobId: string,
  opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<RoadmapJobStatus> {
  const intervalMs = opts.intervalMs ?? 3000;
  const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;
  const deadline = Date.now() + timeoutMs;
  // Tolerate a few consecutive transient failures: one WiFi blip or one 502
  // mid-poll used to abort a healthy ~90s generation and tempt a
  // quota-burning retry (the generation then finished silently). A 404 is
  // not transient — the job doesn't exist or isn't yours — rethrow at once.
  const maxConsecutiveFailures = 3;
  let consecutiveFailures = 0;
  for (let firstCheck = true; ; firstCheck = false) {
    if (!firstCheck) {
      await new Promise((r) => setTimeout(r, intervalMs));
    } // else: check immediately — no reason to pay a full interval up front
    try {
      const job = await getRoadmapJobStatus(jobId);
      consecutiveFailures = 0;
      if (job.status === "complete" || job.status === "failed") {
        return job;
      }
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (err as any)?.response?.status;
      if (status === 404) {
        throw err;
      }
      consecutiveFailures += 1;
      if (consecutiveFailures >= maxConsecutiveFailures) {
        throw err;
      }
    }
    if (Date.now() > deadline) {
      throw new Error("generation_poll_timeout");
    }
  }
}

export async function getLatestRoadmap(): Promise<RoadmapResponse> {
  const { data } = await api.get<RoadmapResponse>("/api/v1/career-transition/roadmap");
  return data;
}

export async function getRoadmapHistory(): Promise<RoadmapHistoryItem[]> {
  const { data } = await api.get<RoadmapHistoryItem[]>(
    "/api/v1/career-transition/roadmap/history",
  );
  return data;
}

export async function getRoadmapById(roadmapId: string): Promise<RoadmapResponse> {
  const { data } = await api.get<RoadmapResponse>(
    `/api/v1/career-transition/roadmap/${roadmapId}`,
  );
  return data;
}

export interface RoadmapTaskCompletion {
  task_id: string;
  completed_at: string;
}

export interface TaskToggleResponse {
  task_id: string;
  completed: boolean;
  completed_at: string | null;
}

export async function getRoadmapTaskCompletions(
  roadmapId: string,
): Promise<RoadmapTaskCompletion[]> {
  const { data } = await api.get<{ completions: RoadmapTaskCompletion[] }>(
    `/api/v1/career-transition/roadmap/${roadmapId}/tasks/completed`,
  );
  return data.completions;
}

export async function toggleRoadmapTask(
  roadmapId: string,
  taskId: string,
  completed: boolean,
): Promise<TaskToggleResponse> {
  const { data } = await api.post<TaskToggleResponse>(
    `/api/v1/career-transition/roadmap/${roadmapId}/tasks/toggle`,
    { task_id: taskId, completed },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Portfolio Kit — user-authored artifacts (Phase B1 backend, Phase B2 UI)
// ---------------------------------------------------------------------------
//
// User-scoped CRUD over /api/v1/portfolio-artifacts. Every endpoint is
// authed via the request interceptor's Bearer token; cross-user access is
// blocked at the router with 404-not-403 (see
// backend/app/routers/portfolio_artifacts.py for the security model).
//
// Rubric fields (rubric_score, rubric_feedback, last_scored_at) are
// read-only from the frontend's perspective — they're populated by the
// Phase B3 scoring endpoint (not yet built). They appear on
// `PortfolioArtifact` here but are absent from `Create` and `Update`
// payloads.

/** Allowed archetype slugs. Mirrors the backend Pydantic Literal. */
export type PortfolioArtifactArchetype =
  | "rag-agent"
  | "multi-agent-researcher"
  | "hitl-workflow-agent"
  | "eval-harness"
  | "mcp-server"
  | "agentic-coding-tool"
  | "conversational-assistant"
  | "fine-tuning-adaptation"
  | "voice-multimodal-agent"
  | "data-embeddings-pipeline";

/** Lifecycle status. Backend defaults new artifacts to "in_progress". */
export type PortfolioArtifactStatus =
  | "in_progress"
  | "complete"
  | "archived";

/** Reachability check result for an artifact's external_url. Returned by
 *  the check-site endpoint and persisted on the artifact (last_site_check_at +
 *  site_check_result) so it surfaces on load, not just right after a run. */
export interface SiteCheckResult {
  status: "ok" | "error_status" | "not_a_web_app" | "unreachable" | "blocked";
  reachable: boolean;
  http_status: number | null;
  final_url: string | null;
  title: string | null;
  response_ms: number | null;
  message: string;
  checked_at: string;
}

/** Full artifact as returned by GET endpoints. Rubric fields are nullable
 *  until the Phase B3 scoring endpoint populates them. */
export interface PortfolioArtifact {
  id: string;
  user_id: string;
  title: string;
  archetype: string; // string (not the union) so legacy/future slugs round-trip
  status: string;
  description: string | null;
  external_url: string | null;

  // Phase B3 fields — null on every artifact until rubric scoring ships.
  rubric_score: Record<string, unknown> | null;
  rubric_feedback: Record<string, unknown> | null;
  last_scored_at: string | null;

  // Site reachability check — null until the learner runs a check.
  last_site_check_at: string | null;
  site_check_result: SiteCheckResult | null;

  created_at: string;
  updated_at: string;
}

/** Create payload. Status defaults to "in_progress" on the backend. */
export interface PortfolioArtifactCreate {
  title: string;
  archetype: PortfolioArtifactArchetype;
  description?: string;
  external_url?: string;
  status?: PortfolioArtifactStatus;
}

/** Update payload. Every field optional — only provided fields are written.
 *  Rubric fields intentionally absent: they're set by the scoring endpoint. */
export interface PortfolioArtifactUpdate {
  title?: string;
  archetype?: PortfolioArtifactArchetype;
  description?: string;
  external_url?: string;
  status?: PortfolioArtifactStatus;
}

/** List artifacts owned by the authenticated user. Newest first.
 *  Empty array (NOT 404) on first use. */
export async function listPortfolioArtifacts(): Promise<PortfolioArtifact[]> {
  const { data } = await api.get<PortfolioArtifact[]>(
    "/api/v1/portfolio-artifacts",
  );
  return data;
}

/** Create a new portfolio artifact. Returns the created row including
 *  server-generated id, user_id, timestamps. */
export async function createPortfolioArtifact(
  payload: PortfolioArtifactCreate,
): Promise<PortfolioArtifact> {
  const { data } = await api.post<PortfolioArtifact>(
    "/api/v1/portfolio-artifacts",
    payload,
  );
  return data;
}

/** Get one artifact by ID. 404 if not found OR not owned by the
 *  requesting user (404-not-403 prevents UUID enumeration). */
export async function getPortfolioArtifact(
  artifactId: string,
): Promise<PortfolioArtifact> {
  const { data } = await api.get<PortfolioArtifact>(
    `/api/v1/portfolio-artifacts/${artifactId}`,
  );
  return data;
}

/** Partial update. Only fields present in the payload are written;
 *  omitted fields are untouched. */
export async function updatePortfolioArtifact(
  artifactId: string,
  payload: PortfolioArtifactUpdate,
): Promise<PortfolioArtifact> {
  const { data } = await api.patch<PortfolioArtifact>(
    `/api/v1/portfolio-artifacts/${artifactId}`,
    payload,
  );
  return data;
}

/** Hard delete. 204 No Content on success. The user can archive
 *  instead by PATCHing status="archived" — that's the reversible path. */
export async function deletePortfolioArtifact(
  artifactId: string,
): Promise<void> {
  await api.delete(`/api/v1/portfolio-artifacts/${artifactId}`);
}

/** Run a reachability check on the artifact's external_url. Returns the
 *  result and persists last_site_check_at + site_check_result on the
 *  artifact server-side (so re-fetching the list surfaces it). */
export async function checkArtifactSite(
  id: string,
): Promise<SiteCheckResult> {
  const { data } = await api.post<SiteCheckResult>(
    `/api/v1/portfolio-artifacts/${id}/check-site`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Portfolio Kit rubric scoring (Phase B3)
// ---------------------------------------------------------------------------
//
// AI-driven quality feedback on a tracked artifact. POST /:id/score runs a
// Claude rubric pass (all_access-gated, rate-limited) and persists the result
// onto the artifact's rubric_score / rubric_feedback JSONB + last_scored_at.
// The fixed five dimensions match the backend (app/schemas/portfolio_rubric.py).

/** The fixed rubric dimension slugs. Must match the backend literal. */
export type RubricDimensionKey =
  | "narrative_clarity"
  | "technical_depth"
  | "evidence_and_metrics"
  | "deployment_and_reachability"
  | "recruiter_readiness";

/** Per-dimension score (1-5) + actionable feedback. */
export interface RubricDimensionScore {
  score: number;
  feedback: string;
}

/** The full rubric pass returned by the scoring endpoint. */
export interface RubricResult {
  dimensions: Record<RubricDimensionKey, RubricDimensionScore>;
  overall_score: number;
  summary: string;
}

/** Response body for POST /{id}/score. */
export interface RubricScoreRead {
  artifact_id: string;
  result: RubricResult;
  last_scored_at: string;
  model: string;
  evidence_source: "repo" | "non_github" | "not_public" | "fetch_failed" | "no_url";
}

/** Run the rubric-scoring pass on an artifact. Returns the fresh result and
 *  persists rubric_score / rubric_feedback / last_scored_at server-side, so
 *  re-fetching the artifact surfaces the score. 403 if not all_access,
 *  404 if not found/owned, 502 on a transient scoring failure. */
export async function scorePortfolioArtifact(
  id: string,
): Promise<RubricScoreRead> {
  const { data } = await api.post<RubricScoreRead>(
    `/api/v1/portfolio-artifacts/${id}/score`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — curriculum + module endpoints
// ---------------------------------------------------------------------------

export interface AgenticModuleSummary {
  slug: string;
  number: number;
  title: string;
  tagline: string;
  studyTime: string;
  tier: string;
  learningOutcomes: string[];
  locked: boolean;
  completed?: boolean;
}

export interface AgenticMilestone {
  id: string;
  number: number;
  title: string;
  summary: string;
  modules: AgenticModuleSummary[];
}

export interface AgenticCurriculum {
  product: string;
  version: string;
  title: string;
  subtitle: string;
  milestones: AgenticMilestone[];
}

export interface AgenticModuleDetail {
  slug: string;
  number: number;
  title: string;
  tagline: string;
  studyTime: string;
  tier: string;
  milestone: { id: string; title: string };
  learningOutcomes: string[];
  locked: boolean;
  markdown: string | null;
  preview: string | null;
  prevSlug: string | null;
  nextSlug: string | null;
  completed?: boolean;
}

export async function getAgenticCurriculum(): Promise<AgenticCurriculum> {
  const { data } = await api.get<AgenticCurriculum>(
    "/api/v1/agentic-mastery/curriculum"
  );
  return data;
}

export async function getAgenticModule(slug: string): Promise<AgenticModuleDetail> {
  const { data } = await api.get<AgenticModuleDetail>(
    `/api/v1/agentic-mastery/modules/${slug}`
  );
  return data;
}

// Agentic Ops (operations mini course) — same response shapes as Mastery.
export async function getAgenticOpsCurriculum(): Promise<AgenticCurriculum> {
  const { data } = await api.get<AgenticCurriculum>("/api/v1/agentic-ops/curriculum");
  return data;
}

export async function getAgenticOpsModule(slug: string): Promise<AgenticModuleDetail> {
  const { data } = await api.get<AgenticModuleDetail>(
    `/api/v1/agentic-ops/modules/${slug}`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — module manifest (v2)
//
// The server-authored segment→asset map. Each segment carries the assets the
// v2 page interleaves inline after that segment of the lesson: NL-debug checks
// (by check_id), walkthrough function references, and a lab flag.
//
// `getModuleManifest` returns null gracefully on ANY error (404 for unknown or
// locked modules, network failure) so the page always falls back to a flat
// render. It must never throw — call-sites depend on that contract.
// ---------------------------------------------------------------------------

export interface SegmentSlide {
  n: number;
  title: string;
  url: string;
}

export interface ModuleManifestSegment {
  title: string;
  anchor: string;
  summary?: string;
  slide_hint?: string;
  walkthrough_fns?: string[];
  check_ids?: string[];
  has_lab?: boolean;
  slides?: SegmentSlide[];
}

export interface ModuleManifest {
  slug: string;
  segments: ModuleManifestSegment[];
}

export async function getModuleManifest(
  slug: string,
): Promise<ModuleManifest | null> {
  try {
    const { data } = await api.get<ModuleManifest>(
      `/api/v1/agentic-mastery/modules/${slug}/manifest`,
    );
    return data;
  } catch {
    // Unknown/locked module (404) or transient failure → fall back to a flat
    // lesson render with a markdown-derived TOC. Never throw.
    return null;
  }
}

/** Agentic Ops manifest — same shape as mastery; drives inline per-segment slides. */
export async function getAgenticOpsModuleManifest(
  slug: string,
): Promise<ModuleManifest | null> {
  try {
    const { data } = await api.get<ModuleManifest>(
      `/api/v1/agentic-ops/modules/${slug}/manifest`,
    );
    return data;
  } catch {
    // Unknown/locked module (404) or transient failure → flat lesson render.
    return null;
  }
}

// ── The Production Harness (claude-harness) ─────────────────────────────────
// Reuses the shared AgenticCurriculum / AgenticModuleDetail / ModuleManifest types.
// Do NOT copy those declarations across — an earlier attempt did and produced a
// duplicate getModuleManifest, which tsc caught as TS2323/TS2393.
export async function getClaudeHarnessCurriculum(): Promise<AgenticCurriculum> {
  const { data } = await api.get<AgenticCurriculum>("/api/v1/claude-harness/curriculum");
  return data;
}

export async function getClaudeHarnessModule(slug: string): Promise<AgenticModuleDetail> {
  const { data } = await api.get<AgenticModuleDetail>(
    `/api/v1/claude-harness/modules/${slug}`
  );
  return data;
}

/** The living-harness changelog (D-013). Inside the entitlement, so 403 for
 * everyone else and 404 when the content file has not been deployed.
 *
 * Returns null on BOTH, deliberately: the caller renders nothing either way, and
 * the alternative — surfacing "not found" to a subscriber during a bad deploy —
 * reads as "David stopped updating it", which is worse than the section being
 * absent. The distinction still exists server-side for debugging.
 */
export async function getHarnessChangelog(): Promise<string | null> {
  try {
    const { data } = await api.get<{ markdown: string }>(
      "/api/v1/claude-harness/harness-changelog",
    );
    return data.markdown;
  } catch {
    return null;
  }
}

export async function getClaudeHarnessModuleManifest(
  slug: string,
): Promise<ModuleManifest | null> {
  try {
    const { data } = await api.get<ModuleManifest>(
      `/api/v1/claude-harness/modules/${slug}/manifest`,
    );
    return data;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — worked-solution walkthrough (in-app gated viewer)
// ---------------------------------------------------------------------------

export interface ModuleWalkthrough {
  slug: string;
  filename: string;
  source: string;
}

export async function getModuleWalkthrough(
  slug: string,
): Promise<ModuleWalkthrough | null> {
  try {
    const { data } = await api.get<ModuleWalkthrough>(
      `/api/v1/agentic-mastery/modules/${slug}/walkthrough`,
    );
    return data;
  } catch {
    // Locked (403) / unknown (404) / transient → caller shows a fallback. Never throws.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — downloadable module resources (homework scaffold/data)
// ---------------------------------------------------------------------------

export interface ResourceFile {
  name: string;
  label: string;
  bytes: number;
}

export interface ModuleResources {
  slug: string;
  anchor: string;
  title: string;
  files: ResourceFile[];
}

export async function getModuleResources(
  slug: string,
): Promise<ModuleResources | null> {
  try {
    const { data } = await api.get<ModuleResources>(
      `/api/v1/agentic-mastery/modules/${slug}/resources`,
    );
    return data;
  } catch {
    return null;
  }
}

/** Fetch a resource as a blob (sends auth) and trigger a browser download. */
export async function downloadModuleResource(
  slug: string,
  filename: string,
): Promise<void> {
  const { data } = await api.get(
    `/api/v1/agentic-mastery/modules/${slug}/resources/${filename}`,
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(new Blob([data as BlobPart]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — formative check (self-assessed "fix these prompts")
// ---------------------------------------------------------------------------

export interface FormativeItem {
  id: string;
  prompt: string;
  context: string;
  answer: string;
  explanation: string;
}

export interface ModuleFormative {
  slug: string;
  anchor: string;
  title: string;
  instructions: string;
  item_label: string;
  choice_label: string;
  reveal_label: string;
  options: string[];
  items: FormativeItem[];
}

export async function getModuleFormative(
  slug: string,
): Promise<ModuleFormative | null> {
  try {
    const { data } = await api.get<ModuleFormative>(
      `/api/v1/agentic-mastery/modules/${slug}/formative`,
    );
    return data;
  } catch {
    // No formative check / locked / transient → render nothing. Never throw.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — progress tracking (Phase 2a)
// ---------------------------------------------------------------------------

export interface AgenticProgress {
  completedSlugs: string[];
  counts: { completed: number; total: number };
  byMilestone: Record<string, { completed: number; total: number }>;
}

export interface CompleteModuleResponse {
  ok: boolean;
  slug: string;
  completedAt: string;
  milestoneComplete: boolean;
  badgeAwarded: boolean;
}

export async function getMasteryProgress(): Promise<AgenticProgress> {
  const { data } = await api.get<AgenticProgress>(
    "/api/v1/agentic-mastery/progress"
  );
  return data;
}

/**
 * Mint a short-TTL deck-access cookie for a gated (all_access) module's deck.
 * Free modules don't need this — their decks are public. Returns 403 if the
 * caller isn't entitled; callers render a locked state on rejection.
 *
 * IMPORTANT: this is a SAME-ORIGIN relative call (not the absolute-baseURL `api`
 * instance) so the browser stores the Set-Cookie on the APP's origin, where the
 * same-origin /decks iframe can then send it back. Routing is provided by the CRA
 * dev proxy (setupProxy.js) and prod nginx. The Bearer token is attached manually
 * because bypassing `api` also bypasses its auth interceptor.
 */
export async function grantDeckAccess(slug: string): Promise<{ granted: boolean }> {
  const token = getAccessToken();
  const { data } = await axios.post<{ granted: boolean }>(
    `/api/v1/decks/${slug}/grant`,
    {},
    {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return data;
}

export async function completeModule(slug: string): Promise<CompleteModuleResponse> {
  const { data } = await api.post<CompleteModuleResponse>(
    `/api/v1/agentic-mastery/modules/${slug}/complete`
  );
  return data;
}

export async function uncompleteModule(slug: string): Promise<{ ok: boolean; slug: string }> {
  const { data } = await api.delete<{ ok: boolean; slug: string }>(
    `/api/v1/agentic-mastery/modules/${slug}/complete`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — inline knowledge-check quiz (Phase 2b)
// ---------------------------------------------------------------------------

export interface AgenticQuizQuestion {
  id: string;
  lo: string;
  stem: string;
  options: string[];
}

export interface AgenticQuiz {
  slug: string;
  count: number;
  questions: AgenticQuizQuestion[];
}

export interface AgenticQuizResultItem {
  id: string;
  correct: boolean;
  correctIndex: number;
  selectedIndex: number | null;
  rationale: string;
}

export interface AgenticQuizResult {
  slug: string;
  score: number;
  total: number;
  passed: boolean;
  results: AgenticQuizResultItem[];
}

export async function getModuleQuiz(
  slug: string,
  product = "agentic-mastery"
): Promise<AgenticQuiz> {
  const { data } = await api.get<AgenticQuiz>(
    `/api/v1/${product}/modules/${slug}/quiz`
  );
  return data;
}

export async function submitModuleQuiz(
  slug: string,
  answers: Record<string, number>,
  product = "agentic-mastery"
): Promise<AgenticQuizResult> {
  const { data } = await api.post<AgenticQuizResult>(
    `/api/v1/${product}/modules/${slug}/quiz/submit`,
    { answers }
  );
  return data;
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — Natural-Language Debug Feedback Widget
// See deliverables/planning/nl_debug_widget_spec.md
// ---------------------------------------------------------------------------

export type DebugVerdict =
  | "on_track"
  | "partial"
  | "off_track"
  | "misconception_match"
  | "cannot_evaluate";

export type DebugNextAction =
  | "iterate"
  | "elaborate"
  | "see_hint"
  | "see_remediation"
  | "skip_or_reveal";

export interface DebugExercise {
  check_id: string;
  exercise_id: string;
  prompt: string;
  code_snippet: string;
  attempt_cap: number;
}

export interface DebugFeedbackResult {
  verdict: DebugVerdict;
  confidence: number;
  feedback: string;
  matched_misconception: string | null;
  matched_concept_tags: string[];
  next_action: DebugNextAction;
  remediation_link: string | null;
  attempts_remaining: number;
  graded_at: string;
}

export interface DebugExerciseSummary {
  check_id: string;
  exercise_id: string;
}

export async function listDebugExercises(
  slug: string
): Promise<DebugExerciseSummary[]> {
  const { data } = await api.get<DebugExerciseSummary[]>(
    `/api/v1/agentic-mastery/modules/${slug}/debug-feedback`
  );
  return data;
}

export async function getDebugExercise(
  slug: string,
  exerciseId: string
): Promise<DebugExercise> {
  const { data } = await api.get<DebugExercise>(
    `/api/v1/agentic-mastery/modules/${slug}/debug-feedback/${exerciseId}`
  );
  return data;
}

export async function submitDebugFeedback(
  slug: string,
  payload: {
    check_id: string;
    exercise_id: string;
    student_response: string;
    attempt_number: number;
  }
): Promise<DebugFeedbackResult> {
  const { data } = await api.post<DebugFeedbackResult>(
    `/api/v1/agentic-mastery/modules/${slug}/debug-feedback`,
    payload
  );
  return data;
}

export interface DebugSolution {
  canonical_fix: string;
  review: { label: string; anchor: string } | null;
}

/** Reveal the canonical solution — only succeeds after the attempt cap (403 before). */
export async function getDebugSolution(
  slug: string,
  exerciseId: string
): Promise<DebugSolution> {
  const { data } = await api.get<DebugSolution>(
    `/api/v1/agentic-mastery/modules/${slug}/debug-feedback/${exerciseId}/solution`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Agentic AI Mastery — Production Projects (Phase 2c)
// ---------------------------------------------------------------------------

export interface AgenticProjectSummary {
  id: string;
  title: string;
  milestone: string;
  tier: string;
  isCapstone: boolean;
  tagline: string;
  locked: boolean;
  scaffoldUrl: string;
  solutionBranch: string;
  bestScore?: number;   // 0..1
  passed?: boolean;
}

export interface AgenticProjectRubricDimension {
  key: string;
  label: string;
  description: string;
}

export interface AgenticProjectSubmission {
  score: number;   // 0..1
  passed: boolean;
  gradedAt: string;
}

export interface AgenticProjectDetail {
  id: string;
  title: string;
  brief: string;
  tagline: string;
  rubric: AgenticProjectRubricDimension[];
  scaffoldUrl: string;
  solutionBranch: string;
  locked: boolean;
  lastSubmission?: AgenticProjectSubmission;
}

export interface AgenticProjectDimension {
  key: string;
  score: number;    // 0..1
  feedback: string;
}

export interface AgenticProjectGradeResult {
  score: number;    // 0..1
  passed: boolean;
  perDimension: AgenticProjectDimension[];
  overallFeedback: string;
  gradedAt: string;
}

export interface AgenticCertificate {
  eligible: boolean;
  requirements: {
    modulesComplete: number;
    modulesTotal: number;
    capstonePassed: boolean;
  };
  issuedAt?: string;
  certificateId?: string;
}

export async function getProjects(): Promise<{ projects: AgenticProjectSummary[] }> {
  const { data } = await api.get<{ projects: AgenticProjectSummary[] }>(
    "/api/v1/agentic-mastery/projects"
  );
  return data;
}

export async function getProject(id: string): Promise<AgenticProjectDetail> {
  const { data } = await api.get<AgenticProjectDetail>(
    `/api/v1/agentic-mastery/projects/${id}`
  );
  return data;
}

export async function submitProject(
  id: string,
  submission: string
): Promise<AgenticProjectGradeResult> {
  const { data } = await api.post<AgenticProjectGradeResult>(
    `/api/v1/agentic-mastery/projects/${id}/submit`,
    { submission }
  );
  return data;
}

export async function getCertificate(): Promise<AgenticCertificate> {
  const { data } = await api.get<AgenticCertificate>(
    "/api/v1/agentic-mastery/certificate"
  );
  return data;
}

// ---------------------------------------------------------------------------
// Forge Coach (agent) — /api/v1/coach. SSE streaming lives in api/coach.ts.
// ---------------------------------------------------------------------------

export interface CoachDossier {
  current_phase: string;
  professional_profile: Record<string, any> | null;
  target_role: Record<string, any> | null;
  constraints_profile: Record<string, any> | null;
  market_intel: Record<string, any> | null;
  gap_analysis: Record<string, any> | null;
  action_plan: Record<string, any> | null;
  last_interaction_at: string | null;
}

export interface CoachMessage {
  role: string;
  content: string;
  phase_at_time: string | null;
  created_at: string;
}

export async function getCoachDossier(): Promise<CoachDossier> {
  const { data } = await api.get<CoachDossier>("/api/v1/coach/dossier");
  return data;
}

export async function getCoachMessages(limit = 50): Promise<CoachMessage[]> {
  const { data } = await api.get<CoachMessage[]>("/api/v1/coach/messages", {
    params: { limit },
  });
  return data;
}

// ---------------------------------------------------------------------------
// AI Tutor — /api/v1/ai-tutor. Scoped Q&A widget on module pages (Path 2B).
// ---------------------------------------------------------------------------

export interface AiTutorReference {
  module_slug: string;
  title: string;
}

export interface AiTutorConversation {
  id: string;
  module_slug: string;
  created_at: string;
  last_message_at: string;
}

export interface AiTutorMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  was_assessment_refused: boolean;
  thumbs: "up" | "down" | null;
  references: AiTutorReference[];
  tokens_input: number | null;
  tokens_output: number | null;
  created_at: string;
}

export interface AiTutorConversationHistory extends AiTutorConversation {
  messages: AiTutorMessage[];
}

export interface AiTutorSendResponse {
  message_id: string;
  content: string;
  was_assessment_refused: boolean;
  references: AiTutorReference[];
  tokens_input: number;
  tokens_output: number;
}

export async function createTutorConversation(
  moduleSlug: string
): Promise<AiTutorConversation> {
  const { data } = await api.post<AiTutorConversation>(
    "/api/v1/ai-tutor/conversations",
    { module_slug: moduleSlug }
  );
  return data;
}

export async function sendTutorMessage(
  conversationId: string,
  question: string
): Promise<AiTutorSendResponse> {
  const { data } = await api.post<AiTutorSendResponse>(
    `/api/v1/ai-tutor/conversations/${conversationId}/messages`,
    { question }
  );
  return data;
}

export async function setTutorThumbs(
  messageId: string,
  thumbs: "up" | "down"
): Promise<{ message_id: string; thumbs: string }> {
  const { data } = await api.post(
    `/api/v1/ai-tutor/messages/${messageId}/thumbs`,
    { thumbs }
  );
  return data;
}

export async function getTutorConversation(
  conversationId: string
): Promise<AiTutorConversationHistory> {
  const { data } = await api.get<AiTutorConversationHistory>(
    `/api/v1/ai-tutor/conversations/${conversationId}`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Portfolio Prompt Studio
// ---------------------------------------------------------------------------

export type PromptArchetype =
  | "rag-agent"
  | "multi-agent-researcher"
  | "hitl-workflow-agent"
  | "eval-harness"
  | "mcp-server"
  | "agentic-coding-tool"
  | "conversational-assistant"
  | "fine-tuning-adaptation"
  | "voice-multimodal-agent"
  | "data-embeddings-pipeline";

export type PromptExperienceLevel = "junior" | "mid" | "senior" | "staff";

export interface PromptProject {
  title: string;
  summary: string;
  metrics: string;
}

export interface PromptIntake {
  name: string;
  target_role: string;
  archetype: PromptArchetype;
  experience_level: PromptExperienceLevel;
  positioning: string;
  tech_stack: string[];
  tone: string;
  domain: string;
  email: string;
  linkedin: string;
  github: string;
  projects: PromptProject[];
  existing_links: string[];
}

export interface GeneratedBlock {
  id: string;
  title: string;
  prompt: string;
}

export interface GeneratedPackage {
  blocks: GeneratedBlock[];
  manual_markdown: string;
  supabase_schema_sql: string;
  llm_wiring_guide_markdown: string;
}

export interface ValidationCheck {
  name: string;
  passed: boolean;
  detail: string;
}

export interface ValidationReport {
  passed: boolean;
  checks: ValidationCheck[];
  readiness_score: number | null;
}

export interface PromptGeneration {
  id: string;
  user_id: string;
  intake: PromptIntake;
  generated_package: GeneratedPackage | null;
  validation_report: ValidationReport | null;
  model: string;
  status: "generating" | "complete" | "failed";
  artifact_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Partial intake the wizard can use to pre-populate fields. Every field is
 *  nullable/absent — the backend returns only what it can infer from the
 *  user's profile, resume, and roadmap. */
export interface IntakePrefill {
  name?: string | null;
  email?: string | null;
  target_role?: string | null;
  experience_level?: PromptExperienceLevel | null;
  tech_stack?: string[] | null;
}

/** Fetch a partial intake to pre-populate the wizard. Never required —
 *  callers should fall back to an empty prefill on error. */
export async function getPortfolioPromptIntakePrefill(): Promise<IntakePrefill> {
  const { data } = await api.get<IntakePrefill>(
    "/api/v1/portfolio-prompt/intake-prefill",
  );
  return data;
}

/** Generate a customized Lovable prompt package from the intake. */
export async function generatePortfolioPrompt(
  payload: PromptIntake,
): Promise<PromptGeneration> {
  const { data } = await api.post<PromptGeneration>(
    "/api/v1/portfolio-prompt/generate",
    payload,
  );
  return data;
}

/** List the authenticated user's generations, newest first. */
export async function listPortfolioPromptGenerations(): Promise<PromptGeneration[]> {
  const { data } = await api.get<PromptGeneration[]>("/api/v1/portfolio-prompt");
  return data;
}

/** Get one generation by id (404 if not found or not owned). */
export async function getPortfolioPromptGeneration(
  id: string,
): Promise<PromptGeneration> {
  const { data } = await api.get<PromptGeneration>(`/api/v1/portfolio-prompt/${id}`);
  return data;
}

/** Save a completed generation into the user's "Your work" tracker as a
 *  portfolio artifact. Returns the created artifact; the generation is
 *  stamped with its artifact_id server-side. */
export async function savePortfolioPromptToArtifact(
  generationId: string,
): Promise<PortfolioArtifact> {
  const { data } = await api.post<PortfolioArtifact>(
    `/api/v1/portfolio-prompt/${generationId}/save-to-artifact`,
  );
  return data;
}

// ─── Resume Workspace ───────────────────────────────────────────────
export interface ResumeProfile {
  id: string;
  version: number;
  structured_profile: Record<string, any>;
  narrative_markdown: string | null;
}
export interface ResumeUsage {
  period: string;
  runs_used: number;
  runs_limit: number;
  tier: string;
  targets_used: number;
  targets_limit: number | null;
}
export type ResumeTargetStatus =
  | "researching" | "applied" | "interviewing" | "offer" | "rejected";
export interface ResumeTarget {
  id: string;
  company: string;
  role_title: string;
  status: ResumeTargetStatus;
  gap_score: number | null;
}
export interface ResumeStepData {
  output: Record<string, any>;
  narrative_markdown: string | null;
  created_at: string | null;
}
export interface ResumeTargetDetail extends ResumeTarget {
  job_description: string;
  steps: Record<string, ResumeStepData>;
}
export interface ResumeStepRunResult {
  step_key: string;
  output: Record<string, any>;
  narrative_markdown: string | null;
}

const RW = "/api/v1/resume-workflow";

export async function getResumeProfile(): Promise<ResumeProfile | null> {
  const { data } = await api.get<ResumeProfile | null>(`${RW}/profile`);
  return data ?? null;
}
export async function buildResumeProfile(source_resume_text: string): Promise<ResumeProfile> {
  const { data } = await api.post<ResumeProfile>(`${RW}/profile`, { source_resume_text });
  return data;
}
export async function uploadResumeProfile(file: File): Promise<ResumeProfile> {
  const form = new FormData();
  form.append("file", file);
  // Let the browser set the multipart Content-Type + boundary (see importAnswers).
  const { data } = await api.post<ResumeProfile>(`${RW}/profile/upload`, form);
  return data;
}
export async function getResumeUsage(): Promise<ResumeUsage> {
  const { data } = await api.get<ResumeUsage>(`${RW}/usage`);
  return data;
}
export async function listResumeTargets(): Promise<ResumeTarget[]> {
  const { data } = await api.get<ResumeTarget[]>(`${RW}/targets`);
  return data;
}
export async function createResumeTarget(payload: {
  company: string;
  role_title: string;
  job_description: string;
}): Promise<ResumeTarget> {
  const { data } = await api.post<ResumeTarget>(`${RW}/targets`, payload);
  return data;
}
export async function getResumeTarget(id: string): Promise<ResumeTargetDetail> {
  const { data } = await api.get<ResumeTargetDetail>(`${RW}/targets/${id}`);
  return data;
}
export async function updateResumeTarget(
  id: string,
  payload: { status?: ResumeTargetStatus; job_description?: string }
): Promise<ResumeTarget> {
  const { data } = await api.patch<ResumeTarget>(`${RW}/targets/${id}`, payload);
  return data;
}
export async function runResumeStep(
  targetId: string,
  stepKey: string,
  inputs: Record<string, any>
): Promise<ResumeStepRunResult> {
  const { data } = await api.post<ResumeStepRunResult>(
    `${RW}/targets/${targetId}/steps/${stepKey}`,
    { inputs }
  );
  return data;
}

export interface ExemplarLookupResponse {
  coverage: "full" | "role_only" | "none";
  refreshed_at: string | null;
  matched_role: string | null;
  matched_company: string | null;
  exemplar: Record<string, any> | null;
  company_overlay_gated?: boolean;
}

export async function getTargetExemplar(targetId: string): Promise<ExemplarLookupResponse> {
  const { data } = await api.get<ExemplarLookupResponse>(`${RW}/targets/${targetId}/exemplar`);
  return data;
}

// --- Gap-score trajectory (stickiness) ---
export interface TrajectoryPoint {
  date: string;
  gap_score: number;
  label: string;
}
export interface TrajectoryResponse {
  points: TrajectoryPoint[];
}
export async function getResumeTrajectory(targetId: string): Promise<TrajectoryResponse> {
  const { data } = await api.get<TrajectoryResponse>(`${RW}/targets/${targetId}/trajectory`);
  return data;
}

// ---------------------------------------------------------------------------
// Career Outcome — skill-gap visualizer (Task 5)
// ---------------------------------------------------------------------------

export interface DimensionOutcome {
  key: string;
  label: string;
  now: number;       // 0–5 current level
  after: number;     // 0–5 after program
  demand: number;    // 0–5 role demand bar
  gap_closed: boolean;
}

export interface OutcomeView {
  track: string;
  role_grounded: boolean;
  dimensions: DimensionOutcome[];
  met_before: number;
  met_after: number;
  total: number;
  gaps_closed: string[];
  show_deployed_systems: boolean;
  expert_path: boolean;
  differentiator: string;
  provenance: string;
  preview: boolean;
}

export async function fetchOutcome(payload: {
  skill_matrix: Record<string, number>;
  target_role: string;
  seniority: string;
  track: string;
}): Promise<OutcomeView> {
  const { data } = await api.post<OutcomeView>(
    "/api/v1/career-transition/outcome",
    payload,
  );
  return data;
}

// --- DOCX export: download the tailored résumé (built from the rewrite steps) ---
export async function downloadResumeDocx(targetId: string): Promise<void> {
  const resp = await api.post(`${RW}/targets/${targetId}/resume.docx`, {}, {
    responseType: "blob",
  });
  const disposition = (resp.headers["content-disposition"] as string) || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "resume.docx";
  const url = window.URL.createObjectURL(resp.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Prompt Admin — registry of versioned product prompts (admin-only)
//
// Backed by the self-prefixing /api/v1/admin/prompts router. Every endpoint is
// admin-gated server-side (403 for non-admins); the AdminRoute guard is the
// matching UX. The instance baseURL is the host root, so paths carry the full
// /api/v1/admin/prompts prefix like the rest of this file.
// ---------------------------------------------------------------------------

export interface PromptSummary {
  product: string;
  prompt_key: string;
  kind: string;
  active_version: number;
  version_id: string;
}

export interface PromptVersionRow {
  id: string;
  version: number;
  status: string;
  created_at: string;
  created_by: string | null;
  activated_at: string | null;
}

export async function adminListPrompts(): Promise<PromptSummary[]> {
  const { data } = await api.get<PromptSummary[]>("/api/v1/admin/prompts");
  return data;
}

export async function adminListVersions(
  product: string,
  key: string,
  kind: string,
): Promise<PromptVersionRow[]> {
  const { data } = await api.get<PromptVersionRow[]>(
    `/api/v1/admin/prompts/${product}/${key}/${kind}/versions`,
  );
  return data;
}

export interface PromptVersionDetail {
  id: string;
  product: string;
  prompt_key: string;
  kind: string;
  version: number;
  status: string;
  content: string;
}

export async function adminGetVersion(id: string): Promise<PromptVersionDetail> {
  const { data } = await api.get<PromptVersionDetail>(
    `/api/v1/admin/prompts/versions/${id}`,
  );
  return data;
}

export async function adminCreateVersion(body: {
  product: string;
  key: string;
  kind: string;
  content: string;
}): Promise<unknown> {
  const { data } = await api.post("/api/v1/admin/prompts", body);
  return data;
}

export async function adminActivateVersion(
  id: string,
  opts?: { eval_run_id?: string; force?: boolean },
): Promise<unknown> {
  // Coach prompts are eval-gated server-side: activation 400s unless an
  // eval_run_id (or force=true) is supplied. Non-coach products ignore the body.
  const { data } = await api.post(
    `/api/v1/admin/prompts/versions/${id}/activate`,
    opts ?? {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Admin metrics — founder-only cross-user counts (North-star + funnel)
// Backend: GET /api/v1/admin/metrics/summary (gated by require_admin)
// ---------------------------------------------------------------------------

export interface AdminFunnelCounts {
  total_users: number;
  new_users_7d: number;
  activated_users: number;
  activation_rate_pct: number;
  progressing_7d: number;
  artifacts_in_progress: number;
  artifacts_complete: number;
  artifacts_complete_live_site: number; // the North-star
  artifact_completion_rate_pct: number;
  paying_users: number;
  generated_at: string;

  // The window these *_in_window figures describe, echoed by the server.
  // Render these, never a client-recomputed range: the server is the authority
  // on what period it actually measured.
  window_from: string;
  window_to: string;
  new_users_in_window: number;
  progressing_in_window: number;
}

/** Half-open [from, to). Both bounds or neither — the API 422s on a lone bound. */
export interface AdminMetricsRange {
  from: string;
  to: string;
}

export async function getAdminMetricsSummary(
  range?: AdminMetricsRange,
): Promise<AdminFunnelCounts> {
  const { data } = await api.get<AdminFunnelCounts>(
    "/api/v1/admin/metrics/summary",
    range ? { params: { from: range.from, to: range.to } } : undefined,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Admin operations metrics — agent_runs ledger + coach/nudge activity (D3).
// Backend: GET /api/v1/admin/metrics/ops (gated by require_admin)
// ---------------------------------------------------------------------------

export interface AdminOpsAgentDaily {
  date: string;
  agent_name: string;
  cost_usd: number;
  runs: number;
  errors: number;
}

export interface AdminOpsMetrics {
  agents: {
    window_days: number;
    daily: AdminOpsAgentDaily[];
    status_mix: { ok: number; error: number; killed: number; capped: number };
  };
  coach: { messages_7d: number; active_dossiers_7d: number };
  nudges: { sent_7d: number; optouts_total: number };
  cache: { note: string };
  generated_at: string;
}

export async function getAdminOpsMetrics(): Promise<AdminOpsMetrics> {
  const { data } = await api.get<AdminOpsMetrics>("/api/v1/admin/metrics/ops");
  return data;
}

// ---------------------------------------------------------------------------
// Admin navigation metrics — §1 read model over page_events (D1).
// Backend: GET /api/v1/admin/metrics/navigation (gated by require_admin)
// ---------------------------------------------------------------------------

export interface AdminNavigationRoute {
  route_template: string;
  views: number | null;
  exits: number | null;
  exit_rate_pct: number | null;
  engaged_ms_median: number | null;
  engaged_ms_p90: number | null;
  // True below the privacy cell-size threshold — every numeric field above is
  // then null. Never render a suppressed cell as 0.
  suppressed: boolean;
  // True when the percentiles above are null for a reason UNRELATED to privacy:
  // too few rows carried an engaged_ms at all (a lost exit beacon leaves it
  // NULL). Distinct from `suppressed`, which is the privacy gate on views —
  // without this the UI cannot tell "no timing data" from "withheld".
  percentiles_suppressed: boolean;
  // Count of rows that contributed an engaged_ms. Null when the row is
  // privacy-suppressed, for the same reason every other count is.
  engaged_samples: number | null;
  // Non-null when the row's numbers are internally inconsistent rather than
  // merely suppressed — e.g. "exits_exceed_views" (lost page_view beacons can
  // leave more closing exits than recorded views). exit_rate_pct is null in
  // this case too, but suppressed stays false: this is a data-quality signal,
  // not a privacy one, and the two must stay distinguishable.
  anomaly: "exits_exceed_views" | null;
}

export interface AdminNavigationMetrics {
  routes: AdminNavigationRoute[];
  dead_end_rate_pct: number | null;
  // Nulled together with dead_end_rate_pct — a session-level cell suppressed
  // by sessions_suppressed, or a zero denominator, both render as unavailable.
  dead_end_sessions: number | null;
  total_sessions: number | null;
  sessions_suppressed: boolean;
  // Rows excluded from EVERY number in this response because their client
  // clock ran implausibly ahead of the server's. Non-zero means the metrics
  // describe less traffic than actually occurred.
  skew_dropped: number;
  coverage_from: string;
  coverage_to: string;
  computed_at: string;
  definition_version: number;
  // Every reason this payload is a partial view of its metrics, e.g.
  // "exit_beacon_lossy" (page_exit delivery is lossy: exit-derived numbers
  // are a lower bound) and "dead_end_404_only" (M-03's OR'd
  // was_redirected_from_protected sub-signal has no frontend producer yet,
  // so the dead-end rate currently reflects 404s only).
  partial: boolean;
  partial_reason?: string[];
}

export async function getAdminNavigationMetrics(
  range?: AdminMetricsRange,
): Promise<AdminNavigationMetrics> {
  const { data } = await api.get<AdminNavigationMetrics>(
    "/api/v1/admin/metrics/navigation",
    range ? { params: { from: range.from, to: range.to } } : undefined,
  );
  return data;
}

export interface AdminContentModule {
  module_slug: string;
  starts: number | null;
  completions: number | null;
  completion_rate_pct: number | null;
  // Opens with no signed-in user. Reported, but deliberately NOT in the rate:
  // a completion requires an account, so anonymous opens measure a population
  // the numerator can never draw from.
  anonymous_opens: number | null;
  suppressed: boolean;
  // Unreachable while the read model's cohort join stands (completions are a
  // subset of starts by construction). Kept as a tripwire on that join.
  anomaly: "completions_exceed_starts" | null;
}

export interface AdminContentMetrics {
  modules: AdminContentModule[];
  // The ONE product with a completion table. A limit, not a default.
  product: string;
  coverage_from: string;
  coverage_to: string;
  computed_at: string;
  definition_version: number;
  partial: boolean;
  partial_reason: string[];
}

export async function getAdminContentMetrics(
  range?: AdminMetricsRange,
): Promise<AdminContentMetrics> {
  const { data } = await api.get<AdminContentMetrics>(
    "/api/v1/admin/metrics/content",
    range ? { params: { from: range.from, to: range.to } } : undefined,
  );
  return data;
}

export interface AdminProductRow {
  product: string;
  // Users whose FIRST-EVER module view was this product. Acquisition is
  // credited to first touch, so a popular second product cannot cannibalise
  // the numbers of whatever actually brought people in.
  acquired: number | null;
  returned: number | null;
  return_rate_pct: number | null;
  suppressed: boolean;
  anomaly: "returned_exceeds_acquired" | null;
}

export interface AdminProductMetrics {
  products: AdminProductRow[];
  return_window_days: number;
  // The cohort's closing edge. Users who first appeared after this cannot yet
  // have returned, and are excluded rather than counted as churned.
  cohort_closes_at: string;
  // True when the selected range is shorter than the return window, so no
  // eligible cohort exists. The panel must say so instead of rendering 0%.
  insufficient_elapsed_time: boolean;
  coverage_from: string;
  coverage_to: string;
  computed_at: string;
  definition_version: number;
  partial: boolean;
  partial_reason: string[];
}

export async function getAdminProductMetrics(
  range?: AdminMetricsRange,
): Promise<AdminProductMetrics> {
  const { data } = await api.get<AdminProductMetrics>(
    "/api/v1/admin/metrics/products",
    range ? { params: { from: range.from, to: range.to } } : undefined,
  );
  return data;
}

export default api;
